import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/system/Box';
import { useStacklineTheme } from '@stackline/ui';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useForm, FormProvider, SubmitHandler, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'src/utils/Hooks/typedHooks';
import AlertConditionsTab from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AlertConditionsTab';
import AlertNotificationsTab from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AlertNotificationsTab';
import { FormAlertConfigSchema } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/EditAlertSchema';
import {
  FormAlertConfigType,
  CreateAlertConfigType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import {
  DEFAULT_ALERT_TEMPLATE,
  useClientDataOnTemplate
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useClientDataOnTemplate';
import {
  useCreateAlert,
  useUpdateAlert,
  useGetAlertById
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useAlertsApi';
import AlertSummaryTab from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AlertSummaryTab';
import {
  AlertsTabsPath,
  determineAlertMode,
  extractAlertIdFromSearchParams,
  type AlertMode
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/utils';
import {
  AlertTemplateIdType,
  CreateYourOwnTemplateId,
  getTemplateConfigByPlatform,
  YourCustomTemplateId
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/template';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';
import { useTemplateStore } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/store/templateStore';

export enum AddEditAlertTab {
  Summary = 'summary',
  Conditions = 'conditions',
  Notifications = 'notifications'
}

export enum AddEditAlertTabsDisplayName {
  summary = 'Summary',
  conditions = 'Conditions',
  notifications = 'Notifications'
}

interface AddEditAlertNavigationTabsProps {
  tab: AddEditAlertTab;
  onTabChange: (tab: AddEditAlertTab) => void;
}

export const AddEditAlertNavigationTabs = ({ tab }: Omit<AddEditAlertNavigationTabsProps, 'onTabChange'>) => {
  return (
    <GenericNavigationTabs
      tab={tab}
      tabs={[
        {
          label: AddEditAlertTabsDisplayName.summary,
          onClick: () => {}, // Disabled - navigation only via Previous/Next
          value: AddEditAlertTab.Summary
        },
        {
          label: AddEditAlertTabsDisplayName.conditions,
          onClick: () => {}, // Disabled - navigation only via Previous/Next
          value: AddEditAlertTab.Conditions
        },
        {
          label: AddEditAlertTabsDisplayName.notifications,
          onClick: () => {}, // Disabled - navigation only via Previous/Next
          value: AddEditAlertTab.Notifications
        }
      ]}
    />
  );
};

const ALERT_TABS = [AddEditAlertTab.Summary, AddEditAlertTab.Conditions, AddEditAlertTab.Notifications] as const;

const AddEditAlertTopNav = ({ mode }: { mode: AlertMode }) => {
  const getHeaderText = () => {
    switch (mode) {
      case 'Edit':
        return 'Edit Alert';
      case 'Create':
      default:
        return 'Create Alert';
    }
  };

  return (
    <Flex justifyContent="flex-start" flexDirection="column">
      <Text variant="h2" sx={{ marginBottom: '8px' }}>
        {getHeaderText()}
      </Text>
    </Flex>
  );
};

type AddEditAlertProps = {
  /**
   * Optional template alert configuration for creating from templates
   * If provided, will override the default configuration
   */
  templateAlert?: Partial<FormAlertConfigType>;
};

const AddEditAlert: React.FC<AddEditAlertProps> = ({ templateAlert }) => {
  const theme = useStacklineTheme();
  const { createAlertAsync, isPending: isCreating } = useCreateAlert();
  const { updateAlertAsync, isPending: isUpdating } = useUpdateAlert();
  const { beaconNavigator } = useBeaconNavigator();
  const location = useLocation();
  const { alertTemplate } = useTemplateStore();
  const [currentTab, setCurrentTab] = useState<AddEditAlertTab>(AddEditAlertTab.Summary);

  // Determine the mode based on URL path
  const mode = determineAlertMode(location.pathname);

  // Extract alertId from search params if in edit mode
  const alertIdFromUrl = mode === 'Edit' ? extractAlertIdFromSearchParams(location.search) : null;

  // Extract templateId from search params if in create mode
  const urlParams = new URLSearchParams(location.search);
  const templateIdFromUrl = mode === 'Create' ? (urlParams.get('templateId') as AlertTemplateIdType) : null;

  // Get the appropriate template configuration
  const getBaseTemplate = (): CreateAlertConfigType => {
    if (templateIdFromUrl && templateIdFromUrl !== CreateYourOwnTemplateId) {
      // Handle custom template from zustand store
      if (templateIdFromUrl === YourCustomTemplateId) {
        // Merge the custom template with default template to ensure all required fields exist
        if (alertTemplate && Object.keys(alertTemplate).length > 0) {
          return alertTemplate as Partial<CreateAlertConfigType>;
        }
        // Fallback to default if store is empty
        return DEFAULT_ALERT_TEMPLATE;
      }

      // Handle regular predefined templates
      const currentAppName = getAppName();
      // Map the app name to supported alert platforms
      const alertPlatform = currentAppName === 'atlas' ? AppName.Atlas : AppName.Beacon;
      const templateConfig = getTemplateConfigByPlatform(templateIdFromUrl, alertPlatform);
      return templateConfig as CreateAlertConfigType;
    }

    // If templateAlert prop is provided (for backward compatibility)
    if (templateAlert) {
      return templateAlert as CreateAlertConfigType;
    }

    return DEFAULT_ALERT_TEMPLATE;
  };

  const baseTemplate = getBaseTemplate();
  const defaultConfigWithClientData = useClientDataOnTemplate(baseTemplate);

  // Fetch existing alert data if we have an alertId
  const {
    data: existingAlertData,
    isLoading: isLoadingAlert,
    error: alertError
  } = useGetAlertById(alertIdFromUrl || '');

  // Priority: 1. API fetched alert data (if editing), 2. Existing prop alert, 3. Default config
  const getFormConfig = useCallback((): FormAlertConfigType | undefined => {
    if (mode === 'Edit' && existingAlertData?.data) {
      // Convert API response to FormAlertConfigType with 'edit' variant
      return {
        ...existingAlertData.data,
        variant: 'edit' as const
      } as FormAlertConfigType;
    }

    if (mode === 'Create' && defaultConfigWithClientData) {
      return defaultConfigWithClientData;
    }

    if (templateAlert) {
      return templateAlert;
    }

    return undefined;
  }, [mode, existingAlertData?.data, defaultConfigWithClientData, templateAlert]);

  const refinedConfig = getFormConfig();

  // Initialize form with the determined configuration
  const methods = useForm<FormAlertConfigType>({
    resolver: zodResolver(FormAlertConfigSchema) as any,
    defaultValues: refinedConfig || undefined,
    mode: 'onChange'
  });

  const {
    handleSubmit,
    formState: { isValid },
    trigger,
    control,
    reset
  } = methods;

  // Watch variant and all form values
  const variant = useWatch({ control, name: 'variant' });
  const formValues = useWatch({ control });

  // Update form values when API data loads (for edit mode)
  useEffect(() => {
    if (mode === 'Edit' && existingAlertData?.data && !isLoadingAlert) {
      const formProfile = {
        ...existingAlertData.data,
        variant: 'edit' as const
      } as FormAlertConfigType;
      reset(formProfile);
    }
  }, [existingAlertData, isLoadingAlert, mode, reset]);

  useEffect(() => {
    console.group('🔍 Alert Form Debug');

    // 1. Log current form JSON values
    console.log('📋 Current Form Values:', JSON.stringify(formValues, null, 2));
    console.log('🔄 Form isValid:', isValid);
    console.log('🔄 Form Errors:', methods.formState.errors);

    // 2. Log validation result using safeParse
    const validationResult = FormAlertConfigSchema.safeParse(formValues);

    if (validationResult.success) {
      console.log('✅ Manual Validation: SUCCESS');
      console.log('✅ Parsed Data:', JSON.stringify(validationResult.data, null, 2));
    } else {
      console.log('❌ Manual Validation: FAILED');
      console.log('❌ Validation Errors:', validationResult.error.errors);

      // More detailed error breakdown
      const errorsByPath = validationResult.error.errors.reduce((acc, error) => {
        const path = error.path.join('.');
        acc[path] = error.message;
        return acc;
      }, {} as Record<string, string>);

      console.log('❌ Errors by Path:', errorsByPath);
    }

    // 3. Compare RHF validation vs manual validation
    if (validationResult.success !== isValid) {
      console.warn('⚠️ VALIDATION MISMATCH!');
      console.warn('Manual validation:', validationResult.success);
      console.warn('RHF isValid:', isValid);
      console.warn('RHF Errors:', methods.formState.errors);
    }

    console.groupEnd();
  }, [formValues, isValid, methods.formState.errors]); // Re-run whenever form values or validation state changes

  const needsDefaultConfig = !templateAlert && mode === 'Create';
  const isConfigReady = templateAlert || defaultConfigWithClientData;
  const isLoadingConfig =
    (mode === 'Edit' && alertIdFromUrl && isLoadingAlert) || (needsDefaultConfig && !isConfigReady);

  if (isLoadingConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Text variant="body1">{mode === 'Edit' ? 'Loading alert data...' : 'Loading alert configuration...'}</Text>
      </Box>
    );
  }

  // Show error state if there was an error fetching the alert
  if (mode === 'Edit' && alertIdFromUrl && alertError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Text variant="body1" color="error">
          Error loading alert: {alertError.message || 'Unknown error occurred'}
        </Text>
      </Box>
    );
  }

  const handleCancel = () => {
    beaconNavigator.setPathname(AlertsTabsPath.myAlerts);
    beaconNavigator.go();
  };

  const getCurrentTabContent = () => {
    switch (currentTab) {
      case AddEditAlertTab.Summary:
        return <AlertSummaryTab />;
      case AddEditAlertTab.Conditions:
        return <AlertConditionsTab<FormAlertConfigType> />;
      case AddEditAlertTab.Notifications:
        return <AlertNotificationsTab />;
      default:
        return <AlertSummaryTab />;
    }
  };

  const handleNext = async () => {
    const currentIndex = ALERT_TABS.indexOf(currentTab);

    // Validate current step before proceeding
    let fieldsToValidate: (keyof FormAlertConfigType)[] = [];

    switch (currentTab) {
      case AddEditAlertTab.Summary:
        fieldsToValidate = ['alertName', 'alertDescription'];
        break;
      case AddEditAlertTab.Conditions:
        fieldsToValidate = ['queryJSON'];
        break;
      case AddEditAlertTab.Notifications:
        fieldsToValidate = ['notifications'];
        break;
      default:
        break;
    }

    // Trigger validation for current step
    if (fieldsToValidate.length > 0) {
      const isStepValid = await trigger(fieldsToValidate);
      if (!isStepValid) {
        return; // Don't proceed if validation fails
      }
    }

    if (currentIndex < ALERT_TABS.length - 1) {
      setCurrentTab(ALERT_TABS[currentIndex + 1]);
    }
  };

  /**
   * This function will not be called if the form is not valid
   * TODO We may need invalidate the cache for GET /alerts
   */
  const rhfOnSubmit: SubmitHandler<FormAlertConfigType> = async (fullAlert) => {
    try {
      if (variant === 'create') {
        await createAlertAsync(fullAlert);
      } else {
        await updateAlertAsync(fullAlert);
      }

      // Navigate back to alerts list on success
      beaconNavigator.setPathname(AlertsTabsPath.myAlerts);
      beaconNavigator.go();
    } catch (error) {
      console.error('Failed to save alert:', error);
    }
  };

  const handlePrevious = () => {
    const currentIndex = ALERT_TABS.indexOf(currentTab);
    if (currentIndex === 0) {
      beaconNavigator.setPathname(AlertsTabsPath.templateAlert);
      beaconNavigator.go();
    }
    if (currentIndex > 0) {
      setCurrentTab(ALERT_TABS[currentIndex - 1]);
    }
  };

  const isLastTab = currentTab === AddEditAlertTab.Notifications;

  return (
    <FormProvider {...methods}>
      <Box component="div">
        <AddEditAlertTopNav mode={mode} />
        <Box marginBottom={theme.spacing.lg}>
          <AddEditAlertNavigationTabs tab={currentTab} />
          <BasePageContainer>
            {getCurrentTabContent()}

            {/* Navigation Buttons */}
            <Flex justifyContent="space-between" alignItems="center" style={{ marginTop: '40px' }}>
              <SlButton variant="normal" onClick={handleCancel}>
                Cancel
              </SlButton>

              <Flex gap={12}>
                <SlButton variant="normal" onClick={handlePrevious}>
                  Previous
                </SlButton>
                {isLastTab ? (
                  <SlButton
                    variant="contained"
                    disabled={!isValid || isCreating || isUpdating}
                    onClick={async () => {
                      // Only submit when button is explicitly clicked
                      // TODO: there is a bug in EmailAutocomplete, that auto submit the form
                      // When email is injected from existing alert
                      if (isValid) {
                        await handleSubmit(rhfOnSubmit)();
                      }
                    }}
                  >
                    {variant === 'edit' ? 'Update Alert' : 'Create Alert'}
                  </SlButton>
                ) : (
                  <SlButton variant="contained" onClick={handleNext}>
                    Next
                  </SlButton>
                )}
              </Flex>
            </Flex>
          </BasePageContainer>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default AddEditAlert;
